/**
 * Phase 2L-A61 — single source of truth for "is this booking paid /
 * otherwise resolved, so the chase-payment UI must be hidden?".
 *
 * Used by:
 *   • Bookings list status badge (renders the green "Paid" pill)
 *   • SendPayLinkButton.isEligible (hides Send / Resend / Enable Reminders)
 *
 * Before this helper existed, the badge used a `||` truthy check while
 * SendPayLinkButton used `=== true`. Legacy rows with `is_paid: 1` (or
 * other truthy-but-not-strict-true values that crept in via early
 * backfill scripts) showed the green Paid pill AND the Send-Pay-Link
 * button at the same time — visually disagreeing with each other. The
 * canonical eligibility now uses identical truthy semantics for every
 * field that signals "no longer chasable".
 */
export function isBookingResolvedForPayment(b) {
  if (!b) return false;
  const status = String(b.status || b.booking_status || "").toLowerCase();
  // Phase 2L-A76 — extended status set to include the post-paid
  // lifecycle states emitted by passport_test progress (in_progress /
  // report_ready / report_published / approved / published). Previously
  // the Lab Queue rendered "Awaiting payment" on bookings whose report
  // had already been published, because their status had advanced past
  // "paid".
  const RESOLVED_STATUSES = new Set([
    "paid", "cancelled", "completed",
    "in_progress", "report_ready", "report_published", "report_sent",
    "approved", "published",
  ]);
  if (RESOLVED_STATUSES.has(status)) return true;
  // Truthy is_paid — covers `true`, `1`, `"true"`, etc.
  if (b.is_paid) return true;
  const ps = String(b.payment_status || "").toLowerCase();
  if (ps === "paid" || ps === "refunded" || ps === "abandoned" || ps === "not_required") return true;
  if (b.no_payment_required === true) return true;
  if (b.complimentary) return true;
  if (String(b.payment_mode || "").toLowerCase() === "comp") return true;
  // Archived rows are also out of the chase-payment surface.
  if (b.archived_at) return true;
  return false;
}

/**
 * True iff the booking is currently a candidate for "send / resend
 * pay link / enable reminders" controls. Combines the resolved gate
 * with a positive eligibility check (must have an amount to chase).
 */
export function isBookingChasable(b) {
  if (!b) return false;
  if (isBookingResolvedForPayment(b)) return false;
  const amount = Number(b.final_amount_payable ?? b.total ?? 0);
  if (!amount || amount <= 0) return false;
  return true;
}

/**
 * Phase 2L-A65 — true iff the booking is comp / no-charge.
 *
 * Used by the booking-detail header pill renderer so comp bookings
 * show a distinct "Complimentary / No payment required" label instead
 * of collapsing onto the generic "Paid" pill. Payment logic, dashboard
 * filters and the chase-payment hiding rules continue to come from
 * `isBookingResolvedForPayment` — this helper is display-only.
 */
export function isBookingNoPaymentRequired(b) {
  if (!b) return false;
  if (b.complimentary) return true;
  if (b.no_payment_required === true) return true;
  if (String(b.payment_mode || "").toLowerCase() === "comp") return true;
  if (String(b.payment_status || "").toLowerCase() === "not_required") return true;
  return false;
}
